import { API_BASE_URL } from '../types';
import { auth } from './firebase';

export type HostedServerStatus =
  | 'provisioning'
  | 'active'
  | 'stopped'
  | 'failed'
  | 'deleting'
  | 'deleted';

export interface HostedActualServer {
  id: string;
  userId: string;
  slug: string;
  status: HostedServerStatus;
  serverUrl: string;
  containerName: string | null;
  dataPath: string | null;
  hostId: string | null;
  name: string | null;
  lastError: string | null;
  lastUsedAt: string | null;
  lastUsedSource: 'nimah' | 'traefik' | string | null;
  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
}

export interface CreateHostedServerResult {
  server: HostedActualServer;
  /** One-time Actual server password. Never stored on Nimah servers. */
  bootstrapPassword?: string;
}

async function authFetch(path: string, init: RequestInit = {}) {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error('AUTH_REQUIRED');
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init.headers || {})
    }
  });

  let data: {
    success?: boolean;
    error?: string;
    server?: HostedActualServer | null;
    ready?: boolean;
    bootstrapPassword?: string;
  } = {};
  try {
    data = await response.json();
  } catch {
    // ignore parse errors
  }

  if (!response.ok) {
    throw new Error(data.error || 'REQUEST_FAILED');
  }

  return data;
}

export async function fetchHostedServer(): Promise<HostedActualServer | null> {
  const data = await authFetch('/actual-hosting/server', { method: 'GET' });
  return data.server ?? null;
}

export async function createHostedServer(): Promise<CreateHostedServerResult> {
  const data = await authFetch('/actual-hosting/server', { method: 'POST' });
  if (!data.server) {
    throw new Error('REQUEST_FAILED');
  }
  return {
    server: data.server,
    ...(typeof data.bootstrapPassword === 'string' && data.bootstrapPassword
      ? { bootstrapPassword: data.bootstrapPassword }
      : {})
  };
}

export async function startHostedServer(): Promise<HostedActualServer> {
  const data = await authFetch('/actual-hosting/server/start', { method: 'POST' });
  if (!data.server) {
    throw new Error('REQUEST_FAILED');
  }
  return data.server;
}

export async function fetchHostedServerTlsReady(): Promise<boolean> {
  const data = await authFetch('/actual-hosting/server/tls-ready', { method: 'GET' });
  return Boolean(data.ready);
}

export async function touchHostedServer(): Promise<HostedActualServer> {
  const data = await authFetch('/actual-hosting/server/touch', { method: 'POST' });
  if (!data.server) {
    throw new Error('REQUEST_FAILED');
  }
  return data.server;
}
